import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _merge from "lodash/merge";
var _dec, _dec2, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9;
import { action, computed, observable } from 'mobx';
import { DEFAULT_QUALITATIVE_PALETTES } from 'app/stores/colors/Colors';
import { getMeasurement } from 'app/stores/dictionary/useDictionaryMeasurements';
import { getRollupKey } from '@kentik/ui-shared/query/query';
import { getApplicationMetadata } from '@kentik/ui-shared/query/queryUtils';
import { getMirroredQuery } from '@kentik/ui-shared/query/mirror';
import QueryRequest from './QueryRequest';
let QueryModel = (_dec = observable.ref, _dec2 = observable.ref, _class = class QueryModel {
  /** Resolved series limit: vizLimit override → query.viz.limit → 10 */
  get effectiveVizLimit() {
    return this.vizLimit ?? this.query.viz?.limit ?? 10;
  }
  constructor(query, settings, options) {
    _initializerDefineProperty(this, "showDebugDialog", _descriptor, this);
    _initializerDefineProperty(this, "dirty", _descriptor2, this);
    this.runSequence = 0;
    this.forceRun = false;
    _initializerDefineProperty(this, "query", _descriptor3, this);
    _initializerDefineProperty(this, "settings", _descriptor4, this);
    this.mainQueryRequest = new QueryRequest({
      queryModel: this,
      type: 'main'
    });
    this.mirrorQueryRequest = new QueryRequest({
      queryModel: this,
      type: 'mirror'
    });
    /** Override metadata merged into URL-derived application_metadata on each run. */
    this.applicationMetadata = void 0;
    /** Entry point for initial/dirty runs and SSE reconnects; overridable (e.g. board load scheduler). */
    this.requestRun = () => this.run();
    /**
     * Series interaction state (visibility, hover).
     * Owned by QueryModel because it is the sole shared MobX observable in the UDE.
     * If QueryModel accumulates more UI concerns in the future, consider extracting
     * these fields into a dedicated SeriesInteractionState child model.
     */
    /** Series visibility map: `row.id` → visible. Absent = visible (default). */
    _initializerDefineProperty(this, "seriesVisibility", _descriptor5, this);
    /** ID of the currently hovered series row, or null. */
    _initializerDefineProperty(this, "hoveredSeriesId", _descriptor6, this);
    /**
     * Client-side override for the number of series to display.
     * Used by the chart renderer to slice results without re-running the query.
     */
    _initializerDefineProperty(this, "vizLimit", _descriptor7, this);
    _initializerDefineProperty(this, "handleShowDebugDialog", _descriptor8, this);
    _initializerDefineProperty(this, "hideDebugDialog", _descriptor9, this);
    this.query = query;
    this.settings = settings;
    this.applicationMetadata = options?.applicationMetadata;
    this.queryRequests.forEach(request => {
      request.query = query;
      let results;
      if (request.type === 'main') {
        results = options?.results;
      }
      if (results) {
        this.dirty = false;
        request.results.replace(results);
        request.rawRows = results.rawRows ?? [];
        request.rawColumnKeys = results.rawColumnKeys ?? [];
        request.rawEventsCollection.replace(request.rawRows);
        request.status = 'done';
      }
    });
  }
  isQueryRequestActive(request) {
    const {
      hasRun,
      type
    } = request;
    if (type === 'main') {
      return true;
    }
    if (type === 'mirror') {
      return this.settings.chart.yAxis?.mirrorMode === true && hasRun;
    }
    return hasRun;
  }
  get queryRequests() {
    return [this.mainQueryRequest, this.mirrorQueryRequest];
  }
  get activeQueryRequests() {
    return this.queryRequests.filter(q => this.isQueryRequestActive(q));
  }
  get hasRun() {
    return this.activeQueryRequests.some(q => q.hasRun);
  }
  get isLoading() {
    return this.activeQueryRequests.some(q => q.isLoading);
  }
  get hasError() {
    return this.activeQueryRequests.some(q => q.hasError);
  }
  get hasResults() {
    return this.activeQueryRequests.some(q => q.hasResults);
  }
  get fullyLoaded() {
    return this.activeQueryRequests.every(q => q.fullyLoaded);
  }
  get status() {
    if (!this.hasRun) {
      return 'init';
    }
    if (this.hasError) {
      return 'error';
    }
    if (this.isLoading && this.hasResults) {
      return 'partial';
    }
    if (this.isLoading) {
      return 'running';
    }
    return 'done';
  }
  get lastResultUpdate() {
    return this.activeQueryRequests.reduce((max, q) => Math.max(max, q.results.lastUpdated ?? 0), 0);
  }

  /** The metric charted in the primary positive y-axis */
  get primaryMetric() {
    const selected = this.settings.chart.yAxis?.metric;
    if (selected) {
      // Check if it's a rollup key (fn_metricName) matching a configured rollup
      const matchingRollup = this.query.rollups.find(r => getRollupKey(r) === selected);
      if (matchingRollup) {
        return matchingRollup.metric;
      }

      // Check if it's a plain metric name (backwards compat)
      const isRegularMetric = this.query.metrics.some(m => m.name === selected);
      if (isRegularMetric) {
        return selected;
      }
    }
    return this.query.metrics[0]?.name ?? '';
  }

  /** The rollup charted in aggregate charts */
  get primaryRollupKey() {
    const selected = this.settings.chart.yAxis?.metric;
    if (selected) {
      // Check if it matches a configured rollup
      const matchingRollup = this.query.rollups.find(r => r.metric === selected);
      if (matchingRollup) {
        return getRollupKey(matchingRollup);
      }
    }
    // Fallback: first rollup key, or plain metric name
    const firstRollup = this.query.rollups[0];
    if (firstRollup) {
      return getRollupKey(firstRollup);
    }
    return this.query.metrics[0]?.name ?? '';
  }
  get allRowIds() {
    const ids = new Set();
    this.activeQueryRequests.forEach(request => {
      request.results.rows.forEach(row => {
        ids.add(row.id);
      });
    });
    return Array.from(ids.values());
  }
  get chartedRowIds() {
    return this.allRowIds.filter(id => this.isRowCharted(id));
  }
  get seriesVisibilityMap() {
    return new Map(this.chartedRowIds.map(id => [id, this.isSeriesVisible(id)]));
  }

  /** Toggle visibility for a single series row.
   *  Deletes the map entry when restoring to visible (absent = visible default),
   *  so `seriesVisibility.size` accurately signals "active selection" for live updates. */
  toggleSeriesVisibility(rowId) {
    const current = this.seriesVisibility.get(rowId);
    if (current === false) {
      this.seriesVisibility.delete(rowId);
    } else {
      this.seriesVisibility.set(rowId, false);
    }
  }

  /** Set visibility for a specific series row (used by cmd+click and shift+click).
   *  Deletes the map entry when setting visible, so only hidden rows occupy the map. */
  setSeriesVisibility(rowId, visible) {
    if (visible) {
      this.seriesVisibility.delete(rowId);
    } else {
      this.seriesVisibility.set(rowId, false);
    }
  }

  /** Show all series (reset visibility map). */
  showAllSeries() {
    this.seriesVisibility.clear();
  }
  hideAllSeries() {
    this.chartedRowIds.forEach(rowId => {
      this.seriesVisibility.set(rowId, false);
    });
  }

  /** Check if a series is visible (absent from map = visible). */
  isSeriesVisible(rowId) {
    return this.seriesVisibility.get(rowId) !== false;
  }
  get colors() {
    return DEFAULT_QUALITATIVE_PALETTES[this.settings.chart.colors ?? 'default'];
  }
  get seriesColors() {
    const {
      colors
    } = this;
    const seriesColors = new Map();
    let colorIndex = 0;
    this.allRowIds.forEach(rowId => {
      if (this.isRowCharted(rowId)) {
        seriesColors.set(rowId, colors[colorIndex % colors.length]);
        colorIndex += 1;
      }
    });
    return seriesColors;
  }
  getSeriesColor(rowId) {
    return this.seriesColors.get(rowId);
  }
  setHoveredSeries(rowId) {
    this.hoveredSeriesId = rowId;
  }
  setChartedRowIds(idMap) {
    this.queryRequests.forEach(request => {
      request.chartedRowIds = new Set(idMap[request.type] || []);
    });
  }

  /** Check if a row has a corresponding chart series/point. */
  isRowCharted(rowId) {
    return this.activeQueryRequests.some(request => request.isRowCharted(rowId));
  }
  buildApplicationMetadata(query = this.query) {
    const baseMetadata = getApplicationMetadata({
      query
    }, window.location.pathname);
    return {
      ...baseMetadata,
      ...(this.applicationMetadata || {}),
      data: {
        ...baseMetadata.data,
        ...(this.applicationMetadata?.data || {})
      }
    };
  }
  async run(queryOverride) {
    const runSequence = this.runSequence + 1;
    this.runSequence = runSequence;
    this.dirty = false;
    this.forceRun = false;

    // Reset series interaction state — user is fetching new data.
    this.seriesVisibility.clear();
    this.hoveredSeriesId = null;

    // set the chart type on the query, and apply any overrides
    const query = _merge({
      viz: {
        type: this.settings.chart.type
      }
    }, this.query, queryOverride || {});

    // TODO I think we have another way of forcing a specific rollup function in UDE? But I can't track it down again
    if (query.viz?.type === 'timelineView') {
      query.rollups.forEach(rollup => {
        rollup.fn = 'min';
      });
    }

    // Derive application_metadata from URL path and merge any overrides (e.g. panel_id).
    const application_metadata = this.buildApplicationMetadata(query);
    const runs = [this.mainQueryRequest.run(query, application_metadata)];
    if (this.settings.chart.yAxis?.mirrorMode) {
      const measurement = await getMeasurement(query.measurement);
      if (this.runSequence !== runSequence) {
        // a new run was started while awaiting, bail out
        return;
      }
      if (measurement) {
        const mirrorQuery = getMirroredQuery(query, measurement);
        runs.push(this.mirrorQueryRequest.run(mirrorQuery, application_metadata));
      } else {
        console.warn(`Unable to mirror query: measurement "${query.measurement}" not found in dictionary.`);
        this.mirrorQueryRequest.status = 'error';
      }
    } else {
      this.mirrorQueryRequest.reset();
    }
    await Promise.all(runs);
  }

  /** Stops the server subscriptions (e.g. live update); last results stay until the next run. */
  unsubscribe() {
    // abandons a run still waiting to send its mirror query
    this.runSequence += 1;
    this.queryRequests.forEach(request => request.unsubscribe());
  }
  cleanup() {
    this.queryRequests.forEach(request => request.cleanup());
    this.runSequence = 0;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "showDebugDialog", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "dirty", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return true;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "query", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "settings", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "seriesVisibility", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return observable.map();
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "hoveredSeriesId", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "vizLimit", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "effectiveVizLimit", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "effectiveVizLimit"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "queryRequests", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "queryRequests"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "activeQueryRequests", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "activeQueryRequests"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasRun", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hasRun"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isLoading", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isLoading"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasError", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hasError"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasResults", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hasResults"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "fullyLoaded", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "fullyLoaded"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "status", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "status"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "lastResultUpdate", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "lastResultUpdate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "primaryMetric", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "primaryMetric"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "primaryRollupKey", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "primaryRollupKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "allRowIds", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "allRowIds"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "chartedRowIds", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "chartedRowIds"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "seriesVisibilityMap", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "seriesVisibilityMap"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggleSeriesVisibility", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggleSeriesVisibility"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setSeriesVisibility", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setSeriesVisibility"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "showAllSeries", [action], Object.getOwnPropertyDescriptor(_class.prototype, "showAllSeries"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hideAllSeries", [action], Object.getOwnPropertyDescriptor(_class.prototype, "hideAllSeries"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "colors", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "colors"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "seriesColors", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "seriesColors"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setHoveredSeries", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setHoveredSeries"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setChartedRowIds", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setChartedRowIds"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "run", [action], Object.getOwnPropertyDescriptor(_class.prototype, "run"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unsubscribe", [action], Object.getOwnPropertyDescriptor(_class.prototype, "unsubscribe"), _class.prototype), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "handleShowDebugDialog", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => this.showDebugDialog = true;
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "hideDebugDialog", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => this.showDebugDialog = false;
  }
}), _class);
export { QueryModel as default };